import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Text from "components/atoms/text/Text";

const PanelBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 0;
    padding: 12px;
`;

const InfoBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px;
    border: solid 1px var(--color-Line);
    border-radius: 4px;
    background-color: var(--color-Base1);
`;

const RevisionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const RevisionCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 12px;
    border: solid 1px ${({ $focused }) => ($focused ? "var(--color-Key)" : "var(--color-Line)")};
    border-radius: 4px;
    background-color: ${({ $focused }) => ($focused ? "var(--color-Base2)" : "var(--color-White)")};
`;

const RevisionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const RevisionTitle = styled(Text).attrs({
    variant: "body-md-500",
    color: "var(--color-SubBlack)",
})``;

const RevisionType = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})`
    flex: 0 0 auto;
`;

const RevisionMeta = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})``;

const Preview = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-SubBlack)",
})`
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
`;

const RestoreButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    align-self: flex-start;
    height: 30px;
    padding: 0 10px;
    border: solid 1px var(--color-Line);
    border-radius: 4px;
    background-color: var(--color-White);
    color: var(--color-SubBlack);
    font-size: 1.2rem;
    font-weight: 500;
    cursor: pointer;

    &:hover:not(:disabled) {
        background-color: var(--color-ButtonHover4);
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const EmptyText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Grey1)",
})`
    padding: 12px 0;
`;

function formatRevisionType(type) {
    if (type === "migrated") return "이관";
    if (type === "manual") return "현재 상태 저장";
    if (type === "restored") return "복원";
    return type || "리비전";
}

function formatDateTime(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function NoteHistoryPanel({ highlightedRevisionId, historyItems = [], isLoading, onRestore, restoreLoading }) {
    const revisionRefs = useRef(new Map());
    const [focusedRevisionId, setFocusedRevisionId] = useState(highlightedRevisionId);

    useEffect(() => {
        if (!highlightedRevisionId || isLoading) return;
        const target = revisionRefs.current.get(String(highlightedRevisionId));
        if (!target) return;
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        const timer = setTimeout(() => setFocusedRevisionId(null), 3000);
        return () => clearTimeout(timer);
    }, [highlightedRevisionId, historyItems, isLoading]);

    return (
        <PanelBody>
            <InfoBox>
                <Text variant="body-md-500" color="var(--color-SubBlack)">
                    저장된 시점
                </Text>
                <Text variant="body-sm-400" color="var(--color-Grey1)">
                    복원하면 선택한 리비전의 내용이 현재 문서에 새 편집으로 적용됩니다.
                </Text>
            </InfoBox>
            {isLoading ? (
                <EmptyText>히스토리를 불러오는 중입니다.</EmptyText>
            ) : historyItems.length ? (
                <RevisionList>
                    {historyItems.map((revision) => (
                        <RevisionCard
                            key={revision.id}
                            ref={(node) => {
                                if (node) revisionRefs.current.set(String(revision.id), node);
                                else revisionRefs.current.delete(String(revision.id));
                            }}
                            data-revision-id={revision.id}
                            data-focused={String(revision.id) === String(focusedRevisionId)}
                            $focused={String(revision.id) === String(focusedRevisionId)}
                        >
                            <RevisionHeader>
                                <RevisionTitle>#{revision.revision_seq}</RevisionTitle>
                                <RevisionType>{formatRevisionType(revision.revision_type)}</RevisionType>
                            </RevisionHeader>
                            <RevisionMeta>
                                {formatDateTime(revision.created_at)}
                                {revision.document_version != null ? ` · doc v${revision.document_version}` : ""}
                            </RevisionMeta>
                            {revision.plain_text_content ? <Preview>{revision.plain_text_content}</Preview> : null}
                            <RestoreButton
                                type="button"
                                onClick={() => onRestore?.(revision)}
                                disabled={restoreLoading}
                            >
                                <Icon name="last" size="13" color="currentColor" />이 시점으로 복원
                            </RestoreButton>
                        </RevisionCard>
                    ))}
                </RevisionList>
            ) : (
                <EmptyText>아직 복원 가능한 리비전이 없습니다.</EmptyText>
            )}
        </PanelBody>
    );
}

export default NoteHistoryPanel;
